import { idbGet, idbSet } from "../cache/idb.js";
import { goesInfoLabel, GOES_PRODUCTS, normalizeSatParam } from "./goesColor.js";
import {
  gcsListUrl,
  gcsObjectUrl,
  parseUtcScanMs,
} from "./glm.js";
import {
  cropIndexWindow,
  goesAvailableOn,
  goesCadenceMs,
  goesHourPrefixes,
  goesHourPrefixesBetween,
  GOES_SECTORS,
  gridCovers,
  goesMesoSatellites,
  MESO_FIT_KM,
  mesoSectorOf,
  GOES_SATELLITES,
  normalizeSatChoice,
  normalizeSatSector,
  pickGoesFile,
  sameGrid,
  selectGoesSource,
  VIEW_MAX_SIZE,
} from "./goesGrid.js";
import {
  cmipPrefixFor,
  cropSeesDay,
  fineProjFor,
  GEO_HIRES_BANDS,
  gridRatio,
  hiresPlan,
  hiresReads,
} from "./goesHires.js";
import { cropCorners } from "./goesRead.js";
import { createTaskPool, GOES_PRIORITY, goesPoolSize } from "./goesQueue.js";

export {
  GOES_SATELLITES,
  normalizeSatChoice,
  goesAvailableOn,
  goesCadenceMs,
  goesInfoLabel,
  GOES_PRIORITY,
  GOES_PRODUCTS,
  GOES_SECTORS,
  normalizeSatParam,
  normalizeSatSector,
};

const MAX_FRAMES_IN_MEMORY = 48;
// Decoded frames held in memory, by bytes as well as count: a screen-sized
// super-res frame can run past 20 MB.
export function frameBudgetBytes(nav = globalThis.navigator) {
  const gb = nav?.deviceMemory || 4;
  return Math.min(512, Math.max(128, gb * 64)) * 1024 * 1024;
}
const MAX_FRAME_BYTES = frameBudgetBytes();
let frameBytes = 0;

const frameCache = new Map();
const listCache = new Map();
// The fixed grid is the same for every file from one satellite and sector, so
// once one file is open the crop window (and cache key) is known up front.
const projCache = new Map();
const workers = [];
let nextId = 1;
let pool = null;

function debugEnabled() {
  try {
    return globalThis.localStorage?.getItem("goesDebug") === "1";
  } catch {
    return false;
  }
}

function projKey(file) {
  // Meso sectors move, so their grid is only reused within the hour.
  if (isMeso(file.sector)) return `${file.bucket}:${file.sector}:${Math.floor(file.startMs / 3600000)}`;
  return `${file.bucket}:${String(file.name).split("/")[0]}`;
}

function windowKey(window) {
  return `${window.x0},${window.y0},${window.x1},${window.y1},${window.stride || 1}`;
}

// `hires` marks GeoColor built from the native-resolution band files.
function frameKey(file, window, product, hires = false) {
  return ["goes3", file.bucket, file.name, windowKey(window), hires ? `${product}+hr` : product].join(":");
}

function spawnWorker(slot) {
  const worker = new Worker(new URL("./goesWorker.js", import.meta.url), { type: "module" });
  const entry = { worker, waiter: null };
  worker.onmessage = (event) => {
    const { id, ok, payload, error } = event.data || {};
    const waiter = entry.waiter;
    if (!waiter || waiter.id !== id) return;
    entry.waiter = null;
    if (ok) waiter.resolve(payload);
    else waiter.reject(new Error(error || "GOES read failed"));
  };
  worker.onerror = (err) => {
    const waiter = entry.waiter;
    entry.waiter = null;
    worker.terminate();
    if (workers[slot] === entry) workers[slot] = null;
    waiter?.reject(err instanceof Error ? err : new Error(err?.message || "GOES worker failed"));
  };
  workers[slot] = entry;
  return entry;
}

function callWorker(slot, message) {
  const entry = workers[slot] || spawnWorker(slot);
  const id = nextId++;
  return new Promise((resolve, reject) => {
    entry.waiter = { id, resolve, reject };
    entry.worker.postMessage({ ...message, id });
  });
}

function getPool() {
  if (!pool) pool = createTaskPool({ size: goesPoolSize(), execute: callWorker });
  return pool;
}

// Files for an hour keep arriving until a few minutes after it ends, so a
// listing of a recent hour goes stale; older hours never change.
const LIVE_LIST_TTL_MS = 60 * 1000;
const LIST_SETTLE_MS = 15 * 60 * 1000;

// ".../2024/118/22/" -> end of that UTC hour.
export function prefixHourEndMs(prefix) {
  const match = /\/(\d{4})\/(\d{3})\/(\d{2})\/$/.exec(prefix);
  if (!match) return Infinity;
  const [, year, doy, hour] = match.map(Number);
  return Date.UTC(year, 0, doy, hour + 1);
}

export function listingExpiresAt(prefix, nowMs) {
  return prefixHourEndMs(prefix) + LIST_SETTLE_MS > nowMs ? nowMs + LIVE_LIST_TTL_MS : Infinity;
}

async function listPrefix(bucket, prefix) {
  const cacheKey = `${bucket}:${prefix}`;
  const hit = listCache.get(cacheKey);
  if (hit && hit.expiresAt > Date.now()) return hit.promise;
  // Listings are shared by every caller, so one caller's abort must not cancel them.
  const pendingList = (async () => {
    const items = [];
    let pageToken = "";
    do {
      const resp = await fetch(gcsListUrl(bucket, prefix, pageToken));
      if (!resp.ok) throw new Error(`GOES list failed (${resp.status})`);
      const json = await resp.json();
      for (const item of json.items || []) {
        if (item?.name) items.push({ name: item.name, size: parseInt(item.size || "0", 10) });
      }
      pageToken = json.nextPageToken || "";
    } while (pageToken);
    return items;
  })();
  listCache.set(cacheKey, { promise: pendingList, expiresAt: listingExpiresAt(prefix, Date.now()) });
  try {
    return await pendingList;
  } catch (err) {
    if (listCache.get(cacheKey)?.promise === pendingList) listCache.delete(cacheKey);
    throw err;
  }
}

async function listGoesFiles(source, centerMs) {
  const prefixes = goesHourPrefixes(centerMs, source.product);
  const pages = await Promise.all(prefixes.map((prefix) => listPrefix(source.sat.bucket, prefix)));
  return pages.flat().map((item) => ({ ...item, bucket: source.sat.bucket, sat: source.sat.id }));
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
}

function forgetFrame(key) {
  const old = frameCache.get(key);
  if (!old) return;
  frameBytes -= old.rgba?.byteLength || 0;
  frameCache.delete(key);
}

function rememberFrame(key, frame) {
  forgetFrame(key);
  frameCache.set(key, frame);
  frameBytes += frame.rgba?.byteLength || 0;
  while (frameCache.size > 1 && (frameCache.size > MAX_FRAMES_IN_MEMORY || frameBytes > MAX_FRAME_BYTES)) {
    forgetFrame(frameCache.keys().next().value);
  }
}

function publishFrame(key, frame) {
  rememberFrame(key, frame);
  const bytes = frame.rgba?.byteLength || 0;
  idbSet(key, frame, Math.max(256, bytes)).catch(() => {});
  return frame;
}

async function cachedFrame(key) {
  if (frameCache.has(key)) return frameCache.get(key);
  const cached = await idbGet(key).catch(() => null);
  const value = cached?.value;
  if (!value?.rgba || !value.proj) return null;
  const rgba = value.rgba instanceof Uint8ClampedArray
    ? value.rgba
    : new Uint8ClampedArray(value.rgba);
  const frame = { ...value, rgba, reason: "ok" };
  rememberFrame(key, frame);
  return frame;
}

const MESO_PRODUCT = "ABI-L2-MCMIPM";
// Meso scans every minute; a farther one means the sector was elsewhere.
const MESO_MATCH_MS = 2 * 60 * 1000;
// National crops load a margin past the viewport so small pans reuse them.
const VIEW_PAD = 0.1;
// A viewport crop keeps about one cell per screen pixel across the padded
// view, decimating only once cells would shrink under 2/3 of a pixel, up to
// the GPU's texture limit and this ceiling.
const VIEW_CELLS_PER_PIXEL = 1.5;
export const VIEW_MAX_CEILING = 3072;

export function viewMaxSize({ width, height, dpr = 1, textureMax = Infinity } = {}) {
  const pixels = Math.max(width, height) * dpr * (1 + (2 * VIEW_PAD));
  if (!(pixels > 0)) return VIEW_MAX_SIZE;
  const wanted = Math.max(VIEW_MAX_SIZE, Math.ceil(pixels * VIEW_CELLS_PER_PIXEL));
  return Math.min(VIEW_MAX_CEILING, textureMax > 0 ? textureMax : Infinity, wanted);
}

function isMeso(sector) {
  return String(sector || "").startsWith("M");
}

function padBounds({ north, south, east, west }, pad) {
  const dLat = (north - south) * pad;
  const dLon = (east - west) * pad;
  return {
    north: Math.min(85, north + dLat),
    south: Math.max(-85, south - dLat),
    east: east + dLon,
    west: west - dLon,
  };
}

function boundsKey(bounds) {
  if (!bounds) return "";
  return ["north", "south", "east", "west"].map((k) => bounds[k].toFixed(1)).join(",");
}

// "auto" and "c" use CONUS when the center is inside its footprint, else Full
// Disk; "f" forces Full Disk. A national view that runs past the CONUS edge
// stays on CONUS: it has the finer cadence, and Full Disk is one click away.
function sourceFor(date, lat, lon, sector, satellite) {
  return selectGoesSource(date, lat, lon, { forceFullDisk: sector === "f", satellite });
}

// The fixed grid of a file, from its metadata alone. A Meso sector can move, so
// its grid is cached per hour and checked against each file that is read.
function fileGrid(file, priority, signal) {
  const cached = projCache.get(projKey(file));
  if (cached) return Promise.resolve(cached);
  const url = gcsObjectUrl(file.bucket, file.name);
  return getPool().run(`grid:${url}`, { type: "grid", url, size: file.size }, {
    priority,
    signal,
    affinity: url,
    finalize: (result) => {
      if (result?.proj) projCache.set(projKey(file), result.proj);
      return result?.proj || null;
    },
  });
}

// A Meso sector holding the area, nearest the scan time, or null.
// With "auto" both East's and West's are checked, the nearer satellite first.
async function resolveMeso({ date, lat, lon, bounds, centerMs, satellite, priority, signal }) {
  const sats = goesMesoSatellites(date, lat, lon, satellite);
  const listings = sats.map((sat) => listGoesFiles({ sat, product: MESO_PRODUCT }, centerMs)
    .then((items) => ({ items }), (err) => ({ err })));
  for (let i = 0; i < sats.length; i++) {
    const sat = sats[i];
    const { items, err } = await listings[i];
    if (err?.name === "AbortError") throw err;
    if (err) continue;
    try {
      for (const sector of ["M1", "M2"]) {
        const picked = pickGoesFile(items.filter((item) => mesoSectorOf(item.name) === sector), centerMs, MESO_MATCH_MS);
        if (!picked) continue;
        const file = { ...picked, sector };
        const proj = await fileGrid(file, priority, signal);
        if (proj && gridCovers(proj, { lat, lon, radiusKm: MESO_FIT_KM, bounds })) {
          return { source: { sector, product: MESO_PRODUCT, sat }, file };
        }
      }
    } catch (err) {
      if (err?.name === "AbortError") throw err;
    }
  }
  return null;
}

async function resolveFile(req) {
  const { date, lat, lon, centerMs, sector, satellite } = req;
  if (sector === "auto" || sector === "m") {
    const meso = await resolveMeso(req);
    if (meso) return meso;
  }
  let source = sourceFor(date, lat, lon, sector, satellite);
  if (!source) return null;
  let files = await listGoesFiles(source, centerMs);
  let file = pickGoesFile(files, centerMs);
  if (!file && source.sector === "C") {
    source = selectGoesSource(date, lat, lon, { forceFullDisk: true, satellite });
    files = await listGoesFiles(source, centerMs);
    file = pickGoesFile(files, centerMs);
  }
  return { source, file: file ? { ...file, sector: source.sector } : null };
}

// The single-band CMIP files GeoColor reads at native resolution, or null when
// any is missing. Each is one exact-prefix listing.
async function hiresFiles(file) {
  try {
    const found = await Promise.all(GEO_HIRES_BANDS.map(async (band) => {
      const prefix = cmipPrefixFor(file.name, band);
      if (!prefix) return null;
      const [item] = await listPrefix(file.bucket, prefix);
      return item ? { band, url: gcsObjectUrl(file.bucket, item.name), size: item.size } : null;
    }));
    return found.every(Boolean) ? found : null;
  } catch {
    return null;
  }
}

// GeoColor's day/night blend uses the scan's own time, so frames that share
// a file share one image.
function scanTimeMs(file) {
  return Number.isFinite(file.startMs) ? (file.startMs + file.endMs) / 2 : null;
}

function frameMeta(file, { lat, lon, range, product }) {
  return {
    lat,
    lon,
    rangeKm: range,
    sat: file.sat,
    sector: file.sector,
    product,
    startMs: file.startMs,
    endMs: file.endMs,
    name: file.name,
  };
}

// Super-res GeoColor. Each band file is read by its own pool job, so the reads
// run side by side, and one more job composes them. Null to fall back to the
// 2 km crop: at night, when 2 km is already as fine as the view, or when a
// band file is missing or fails.
async function readSuperResFrame(file, request) {
  const { lat, lon, range, bounds, maxSize, product, priority, signal } = request;
  const proj = await fileGrid(file, priority, signal);
  if (!proj) return null;
  const window = cropIndexWindow({ lat, lon, rangeKm: range, bounds, maxSize, proj });
  if (!window) return { reason: "outside", product, sat: file.sat };
  const key = frameKey(file, window, product, true);
  const cached = await cachedFrame(key);
  if (cached) return cached;
  const timeMs = scanTimeMs(file);
  const plan = hiresPlan(window, proj, fineProjFor(proj), maxSize);
  if (!plan || !cropSeesDay(window, proj, timeMs)) return null;
  const files = await hiresFiles(file);
  throwIfAborted(signal);
  if (!files) return null;
  const reads = hiresReads(plan, files, { url: gcsObjectUrl(file.bucket, file.name), size: file.size });
  const started = performance.now();
  let results;
  try {
    results = await Promise.all(reads.map((read) => getPool().run(
      `band:${read.url}|${windowKey(read.window)}`,
      { type: "band", band: read.band, url: read.url, size: read.size, window: read.window },
      { priority, signal, affinity: read.url },
    )));
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
  const fine = results[0].proj;
  // A Meso sector that moved since its grid was cached, or a band file on
  // some other grid.
  if (!sameGrid(results[results.length - 1].proj, proj) || gridRatio(proj, fine) !== plan.factor) return null;
  const composed = await getPool().run(`compose:${key}`, {
    type: "compose",
    plan,
    product,
    timeMs,
    reads: results.map((result, i) => ({ ...result, ratio: reads[i].ratio })),
  }, { priority, signal });
  if (composed?.reason !== "ok") return null;
  if (debugEnabled()) {
    const bytes = results.reduce((sum, result) => sum + (result.stats?.bytes || 0), 0);
    const requests = results.reduce((sum, result) => sum + (result.stats?.requests || 0), 0);
    console.info("[goes]", product, file.name.split("/").pop(), priority, windowKey(plan.window),
      JSON.stringify({ requests, bytes, ms: Math.round(performance.now() - started), bands: results.map((r) => r.stats?.ms) }));
  }
  return publishFrame(key, {
    reason: "ok",
    rgba: composed.rgba,
    width: plan.width,
    height: plan.height,
    x0: plan.window.x0,
    y0: plan.window.y0,
    stride: plan.window.stride,
    // The 2 km window stays the frame's identity: cache keys and coverage
    // checks are worked out on the 2 km grid.
    window,
    proj: composed.proj,
    baseProj: proj,
    timeMs,
    corners: cropCorners(plan.window, composed.proj),
    ...frameMeta(file, request),
  });
}

async function readFrame(file, request) {
  if (request.product === "geosr") {
    const frame = await readSuperResFrame(file, request);
    if (frame) return frame;
  }
  return readPlainFrame(file, request);
}

async function readPlainFrame(file, { lat, lon, range, bounds, maxSize, product, priority, signal }) {
  const proj = projCache.get(projKey(file));
  const window = proj ? cropIndexWindow({ lat, lon, rangeKm: range, bounds, maxSize, proj }) : null;
  if (proj && !window) return { reason: "outside", product, sat: file.sat };
  if (window) {
    const cached = await cachedFrame(frameKey(file, window, product));
    if (cached) return cached;
  }
  const url = gcsObjectUrl(file.bucket, file.name);
  // Before the grid is known, share on the request itself.
  const jobKey = window
    ? frameKey(file, window, product)
    : `${url}|${lat.toFixed(2)},${lon.toFixed(2)},${Math.round(range)}|${boundsKey(bounds)}|${product}`;
  const timeMs = scanTimeMs(file);
  return getPool().run(jobKey, {
    type: "read",
    url,
    size: file.size,
    lat,
    lon,
    rangeKm: range,
    bounds,
    maxSize,
    window,
    grid: proj,
    product,
    timeMs,
  }, {
    priority,
    signal,
    affinity: url,
    finalize: (crop) => {
      if (crop?.proj) projCache.set(projKey(file), crop.proj);
      if (crop?.stats && debugEnabled()) {
        console.info("[goes]", product, file.name.split("/").pop(), priority, crop.window ? windowKey(crop.window) : crop.reason, JSON.stringify(crop.stats));
      }
      if (crop?.reason !== "ok") return { ...crop, product, sat: file.sat };
      const frame = { ...crop, ...frameMeta(file, { lat, lon, range, product }) };
      delete frame.stats;
      return publishFrame(frameKey(file, crop.window, product), frame);
    },
  });
}

// Loads the GOES image nearest date/time. Site view passes lat/lon/rangeKm;
// national view passes the viewport as `bounds` ({north, south, east, west}),
// which is padded and decimated to `maxSize` cells a side.
export async function loadGoesFrame({
  lat,
  lon,
  rangeKm,
  bounds,
  maxSize = VIEW_MAX_SIZE,
  date,
  time,
  product = "geo",
  sector = "auto",
  satellite = "auto",
  priority = "current",
  signal,
} = {}) {
  const satProduct = normalizeSatParam(product) || "geo";
  const satSector = normalizeSatSector(sector);
  const satChoice = normalizeSatChoice(satellite);
  if (!goesAvailableOn(date)) return { reason: "unavailable", product: satProduct };
  const centerMs = parseUtcScanMs(date, time);
  const range = Number(rangeKm) > 0 ? Number(rangeKm) : 300;
  const area = bounds ? padBounds(bounds, VIEW_PAD) : null;
  const cLat = Number.isFinite(lat) ? lat : (area ? (area.north + area.south) / 2 : NaN);
  const cLon = Number.isFinite(lon) ? lon : (area ? (area.east + area.west) / 2 : NaN);
  if (!Number.isFinite(cLat) || !Number.isFinite(cLon) || centerMs == null) {
    return { reason: "empty", product: satProduct };
  }

  try {
    throwIfAborted(signal);
    const where = {
      date,
      lat: cLat,
      lon: cLon,
      bounds: area,
      centerMs,
      satellite: satChoice,
      priority,
      signal,
    };
    let resolved = await resolveFile({ ...where, sector: satSector });
    throwIfAborted(signal);
    if (!resolved) return { reason: "unavailable", product: satProduct };
    if (!resolved.file) return { reason: "empty", product: satProduct, sat: resolved.source.sat.id };

    const request = {
      lat: cLat,
      lon: cLon,
      range,
      bounds: area,
      maxSize,
      product: satProduct,
      priority,
      signal,
    };
    let frame = await readFrame(resolved.file, request);
    // The Meso sector moved off the area since its grid was cached.
    if (frame.reason === "outside" && isMeso(resolved.source.sector)) {
      resolved = await resolveFile({ ...where, sector: "c" });
      if (!resolved?.file) return frame;
      frame = await readFrame(resolved.file, request);
    }
    if (frame.reason !== "outside" || resolved.source.sector !== "C") return frame;

    const full = selectGoesSource(date, cLat, cLon, { forceFullDisk: true, satellite: satChoice });
    const fullFile = pickGoesFile(await listGoesFiles(full, centerMs), centerMs);
    throwIfAborted(signal);
    if (!fullFile) return { reason: "outside", product: satProduct, sat: full.sat.id };
    return await readFrame({ ...fullFile, sector: full.sector }, request);
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return { reason: "error", message: err.message, product: satProduct };
  }
}

// Whether a loaded national frame still covers this viewport at the right
// detail, so a pan or zoom can skip the reload.
export function goesFrameCovers(frame, bounds, maxSize = VIEW_MAX_SIZE) {
  const have = frame?.window;
  const proj = frame?.baseProj || frame?.proj;
  if (!have || !proj || !bounds) return false;
  const want = cropIndexWindow({ proj, bounds, maxSize });
  if (!want) return true;
  const inside = want.x0 >= have.x0 && want.x1 <= have.x1
    && want.y0 >= have.y0 && want.y1 <= have.y1;
  if (!inside) return false;
  // Detail is judged on the crop a reload would ask for: the padded view.
  const next = cropIndexWindow({ proj, bounds: padBounds(bounds, VIEW_PAD), maxSize }) || want;
  if (next.stride !== (have.stride || 1)) return false;
  if (frame.product !== "geosr") return true;
  // SuperRes can get finer on zoom-in while the 2 km stride stays 1.
  const plan = hiresPlan(next, proj, fineProjFor(proj), maxSize);
  if (!plan) return true;
  if (frame.baseProj) return frame.stride <= plan.window.stride;
  // A 2 km stand-in (night, or a failed band read) for this same crop.
  return windowKey(next) === windowKey(have);
}

// The satellite clock: every scan of the chosen sector between startMs and
// endMs, oldest first. Satellite-only mode steps through these. Auto uses the
// Meso sector holding the area at `sectorAtMs` (default endMs), when there is one.
export async function listGoesScans({
  date,
  lat,
  lon,
  bounds,
  startMs,
  endMs,
  sectorAtMs,
  sector = "auto",
  satellite = "auto",
  signal,
} = {}) {
  if (!goesAvailableOn(date) || !Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];
  const satSector = normalizeSatSector(sector);
  const satChoice = normalizeSatChoice(satellite);
  const area = bounds ? padBounds(bounds, VIEW_PAD) : null;
  const cLat = Number.isFinite(lat) ? lat : (area ? (area.north + area.south) / 2 : NaN);
  const cLon = Number.isFinite(lon) ? lon : (area ? (area.east + area.west) / 2 : NaN);
  if (!Number.isFinite(cLat) || !Number.isFinite(cLon)) return [];
  let source = null;
  if (satSector === "auto" || satSector === "m") {
    const meso = await resolveMeso({
      date,
      lat: cLat,
      lon: cLon,
      bounds: area,
      centerMs: Number.isFinite(sectorAtMs) ? sectorAtMs : endMs,
      satellite: satChoice,
      priority: "current",
      signal,
    });
    source = meso?.source || null;
  }
  source = source || sourceFor(date, cLat, cLon, satSector, satChoice);
  if (!source) return [];
  const prefixes = goesHourPrefixesBetween(startMs, endMs, source.product);
  const pages = await Promise.all(prefixes.map((prefix) => listPrefix(source.sat.bucket, prefix)));
  const scans = [];
  for (const item of pages.flat()) {
    if (isMeso(source.sector) && mesoSectorOf(item.name) !== source.sector) continue;
    const file = pickGoesFile([item], startMs, Infinity);
    if (!file || file.startMs < startMs || file.startMs > endMs) continue;
    scans.push({
      name: item.name,
      sat: source.sat.id,
      sector: source.sector,
      startMs: file.startMs,
      endMs: file.endMs,
    });
  }
  return scans.sort((a, b) => a.startMs - b.startMs);
}
